import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { formatCurrency, formatDate } from '@/utils/format';
import { Car, Calendar } from 'lucide-react';

function CarRentalSelector({ checkIn, checkOut, onCarSelect, selectedCar }) {
  const [carTypes, setCarTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [carStartDate, setCarStartDate] = useState(checkIn);
  const [carEndDate, setCarEndDate] = useState(checkOut);
  const [showCarOptions, setShowCarOptions] = useState(false);

  useEffect(() => {
    if (showCarOptions) {
      fetchAvailableCarTypes();
    }
  }, [showCarOptions, carStartDate, carEndDate]);

  useEffect(() => {
    // Reset car dates when room dates change
    setCarStartDate(checkIn);
    setCarEndDate(checkOut);
  }, [checkIn, checkOut]);

  const fetchAvailableCarTypes = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/car-types/available`, {
        params: {
          start_date: carStartDate,
          end_date: carEndDate
        }
      });
      setCarTypes(response.data);
    } catch (error) {
      console.error('Error fetching car types:', error);
      toast.error('Failed to load available car types');
    } finally {
      setLoading(false);
    }
  };

  const calculateCarDays = () => {
    const start = new Date(carStartDate);
    const end = new Date(carEndDate);
    return Math.ceil((end - start) / (1000 * 60 * 60 * 24));
  };

  const handleCarTypeSelection = (carType) => {
    // Use pre-calculated values from API (which includes date-specific rates)
    const days = carType.days || calculateCarDays();
    const dailyRate = carType.base_daily_rate || 0;
    const totalAmount = carType.total_price || (dailyRate * days);
    
    onCarSelect({
      car_id: carType.sample_car_id,  // Use the sample car from the available type
      car_type_id: carType.id,
      car_name: carType.name,
      car_start_date: carStartDate,
      car_end_date: carEndDate,
      car_daily_rate: dailyRate,
      car_days: days,
      car_amount: totalAmount  // Use server-calculated total with date-specific rates
    });
  };

  const handleRemoveCar = () => {
    onCarSelect(null);
    setShowCarOptions(false);
  };

  if (!showCarOptions && !selectedCar) {
    return (
      <Card className="mt-4 border-2 border-dashed border-blue-300 bg-gradient-to-r from-blue-50 to-indigo-50 hover:border-blue-500 hover:shadow-lg transition-all">
        <CardContent className="p-6">
          <button
            type="button"
            onClick={() => setShowCarOptions(true)}
            className="w-full flex flex-col items-center gap-3 group"
          >
            <div className="p-4 bg-blue-600 rounded-full group-hover:bg-blue-700 transition-colors shadow-lg">
              <Car className="w-8 h-8 text-white" />
            </div>
            <div className="text-center">
              <h3 className="text-xl font-bold text-blue-900 mb-1">
                Add Car Rental to Your Stay
              </h3>
              <p className="text-sm text-blue-700">
                Choose from Economy to Luxury cars • From €35/day
              </p>
            </div>
            <div className="flex items-center gap-2 text-blue-600 font-medium mt-2">
              <span>Browse Available Car Types</span>
              <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </button>
        </CardContent>
      </Card>
    );
  }

  if (selectedCar) {
    return (
      <Card className="mt-4 border-2 border-blue-200 bg-blue-50">
        <CardContent className="p-4">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-blue-600 rounded-lg">
                <Car className="w-6 h-6 text-white" />
              </div>
              <div>
                <h4 className="font-semibold text-lg">{selectedCar.car_name}</h4>
                <p className="text-sm text-gray-600 mt-1">
                  {formatDate(selectedCar.car_start_date)} - {formatDate(selectedCar.car_end_date)}
                </p>
                <p className="text-sm text-gray-600">
                  {selectedCar.car_days} day{selectedCar.car_days !== 1 ? 's' : ''} × {formatCurrency(selectedCar.car_daily_rate)}
                </p>
                <p className="text-lg font-bold text-blue-600 mt-2">
                  {formatCurrency(selectedCar.car_amount)}
                </p>
                <p className="text-xs text-amber-700 mt-1 italic">Payable at reception upon arrival</p>
              </div>
            </div>
            <button
              type="button"
              onClick={handleRemoveCar}
              className="text-red-600 hover:text-red-700 text-sm font-medium"
            >
              Remove
            </button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="mt-4">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-semibold flex items-center gap-2">
            <Car className="w-5 h-5" />
            Select Car Type
          </h4>
          <button
            type="button"
            onClick={() => setShowCarOptions(false)}
            className="text-gray-500 hover:text-gray-700 text-sm"
          >
            Cancel
          </button>
        </div>

        {/* Car Rental Dates */}
        <div className="grid grid-cols-2 gap-3 mb-4 p-3 bg-gray-50 rounded-lg">
          <div>
            <Label htmlFor="car_start_date" className="text-xs">Pickup Date</Label>
            <Input
              id="car_start_date"
              type="date"
              value={carStartDate}
              min={checkIn}
              max={checkOut}
              onChange={(e) => setCarStartDate(e.target.value)}
              className="mt-1"
            />
            <p className="text-xs text-gray-600 mt-1">{formatDate(carStartDate)}</p>
          </div>
          <div>
            <Label htmlFor="car_end_date" className="text-xs">Return Date</Label>
            <Input
              id="car_end_date"
              type="date"
              value={carEndDate}
              min={carStartDate}
              max={checkOut}
              onChange={(e) => setCarEndDate(e.target.value)}
              className="mt-1"
            />
            <p className="text-xs text-gray-600 mt-1">{formatDate(carEndDate)}</p>
          </div>
          <div className="col-span-2 text-center text-sm text-gray-600">
            {calculateCarDays()} day{calculateCarDays() !== 1 ? 's' : ''} rental
          </div>
        </div>

        {/* Available Car Types */}
        {loading ? (
          <div className="text-center py-4 text-gray-500">Loading car types...</div>
        ) : carTypes.length === 0 ? (
          <div className="text-center py-4 text-gray-500">
            No car types available for selected dates
          </div>
        ) : (
          <div className="space-y-3">
            {carTypes.map((carType) => {
              // Use API-provided values (with date-specific rates)
              const days = carType.days || calculateCarDays();
              const dailyRate = carType.base_daily_rate || 0;
              const totalPrice = carType.total_price || (dailyRate * days);
              return (
                <button
                  key={carType.id}
                  type="button"
                  onClick={() => handleCarTypeSelection(carType)}
                  className="w-full p-4 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all text-left"
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h5 className="font-semibold text-lg">{carType.name}</h5>
                        <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full font-medium">
                          {carType.available_count} available
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">{carType.description}</p>
                      <p className="text-sm text-gray-500 mt-2">
                        {formatCurrency(dailyRate)}/day avg
                      </p>
                    </div>
                    <div className="text-right ml-4">
                      <div className="font-bold text-xl text-blue-600">
                        {formatCurrency(totalPrice)}
                      </div>
                      <div className="text-xs text-gray-500">
                        for {days} day{days !== 1 ? 's' : ''}
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default CarRentalSelector;
